import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search, SyncProblem } from '@mui/icons-material';
import { type GridColDef } from '@mui/x-data-grid';
import { GridToolbar } from '@mui/x-data-grid/internals';
import { useTranslation } from 'react-i18next';
import COLORS from '@/resources/style/Themes';
import { StringKeys } from '@/resources/strings/StringKeys';
import { NavigatorDataGrid } from '@/components/data/NavigatorDataGrid';
import { formatDeviceTypeLabel } from '@/utils/device_helper/formatDeviceTypeLabel';
import { formatLastSeenAt } from '@/utils/device_helper/formatLastSeenAt';
import { buildDeviceOverride } from '@/utils/device_helper/buildDeviceOverride';
import { isAuthIdInOrgGroups } from '@/utils/device_helper/isAuthIdInOrgGroups';
import { useDashboardActions } from '@/stores/map/dashboardStore';
import BatteryIndicator from '@/views/UserViews/home/user_dashboard/user_dashboard_components/shared/BatteryIndicator';
import { getFirmwareStatus } from '@/views/UserViews/home/user_dashboard/user_dashboard_components/utils';
import LastUserCell from '@/components/device/LastUserCell';
import type { OrgCompassDeviceListItem } from '@/models/OrgCompassDevice/OrgCompassDeviceListItem';
import Group from '@/models/Organization/Group';
import type {
  CompassInfoRequest,
  FirmwareLookupTableOnAzure,
} from '@/../generated-client/models';

type DeviceTableProps = {
  devices: OrgCompassDeviceListItem[];
  groups: Group[];
  firmwareLookup?: FirmwareLookupTableOnAzure | null;
};

const DEFAULT_PAGE_SIZE = 15;
const PAGE_SIZE_OPTIONS = [15, 25, 50];
const FIRMWARE_OUTDATED_ICON_SIZE = 20;
const MILLIS_PER_SECOND = 1000;
const OVERRIDE_TIMESTAMP_UNAVAILABLE = 0;

function getOverrideTimestampMs(row: OrgCompassDeviceListItem): number | null {
  const seconds = buildDeviceOverride(row).compassInfoSnapshot.timestamp;
  return seconds > OVERRIDE_TIMESTAMP_UNAVAILABLE
    ? seconds * MILLIS_PER_SECOND
    : null;
}

const isDeviceFirmwareOutdated = (
  firmwareRevision: string | null,
  firmwareLookup: FirmwareLookupTableOnAzure | null | undefined,
) => {
  if (!firmwareLookup) return false;
  return getFirmwareStatus(
    { firmware_revision: firmwareRevision } as CompassInfoRequest,
    firmwareLookup,
  ).firmwareOutdated;
};

const FirmwareRevisionDisplay = ({
  firmwareRevision,
  firmwareOutdated,
}: {
  firmwareRevision: string | null;
  firmwareOutdated: boolean;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    {firmwareOutdated && (
      <SyncProblem
        sx={{ fontSize: FIRMWARE_OUTDATED_ICON_SIZE, color: COLORS.RED }}
      />
    )}
    <Typography component="span" variant="body2">
      {firmwareRevision ?? '--'}
    </Typography>
  </Box>
);

const DeviceTable = ({
  devices,
  groups,
  firmwareLookup = null,
}: DeviceTableProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dashboardActions = useDashboardActions();
  const [searchQuery, setSearchQuery] = useState('');

  const openDevice = (row: OrgCompassDeviceListItem) => {
    dashboardActions.openDashboard(row.last_auth_id, buildDeviceOverride(row));
  };

  const columns = useMemo<GridColDef<OrgCompassDeviceListItem>[]>(
    () => [
      {
        field: 'serial_number',
        headerName: t(StringKeys.SERIAL_NUMBER),
        headerClassName: 'first',
        minWidth: 140,
        flex: 0.75,
        display: 'flex',
        valueGetter: (_, row) => row.serial_number ?? '--',
      },
      {
        field: 'mac_address',
        headerName: t(StringKeys.MAC_ADDRESS),
        minWidth: 150,
        flex: 0.75,

        display: 'flex',
      },
      {
        field: 'device_type',
        headerName: t(StringKeys.DEVICE_TYPE),
        minWidth: 140,
        flex: 0.75,
        display: 'flex',
        valueGetter: (_, row) => formatDeviceTypeLabel(row.hardware_version),
      },
      {
        field: 'battery_percentage',
        headerName: t(StringKeys.BATTERY),
        minWidth: 120,
        flex: 0.5,
        display: 'flex',

        // Keep null as null so type:'number' sorting stays numeric.
        // Display ("N/A" / "92%") lives in BatteryIndicator via renderCell;
        // do NOT return '--' from valueGetter (string breaks numeric sort).
        valueGetter: (_, row) => row.battery_percentage ?? null,
        renderCell: ({ row }) => (
          <BatteryIndicator percentage={row.battery_percentage ?? 0} />
        ),
      },
      {
        field: 'last_seen_at',
        headerName: t(StringKeys.DEVICE_LAST_CONNECTED),
        minWidth: 180,
        flex: 0.75,
        display: 'flex',
        valueGetter: (_, row) => getOverrideTimestampMs(row),
        renderCell: ({ row }) => (
          <Typography component="span" variant="body2">
            {formatLastSeenAt(row.last_seen_at)}
          </Typography>
        ),
      },
      {
        field: 'last_user_display_name',
        headerName: t(StringKeys.LAST_USER),
        minWidth: 150,
        flex: 0.75,
        display: 'flex',
        valueGetter: (_, row) => row.last_user_display_name ?? '--',
        renderCell: ({ row }) => (
          <LastUserCell
            displayName={row.last_user_display_name}
            showLeftOrgIcon={
              !!row.last_user_display_name &&
              !!row.last_auth_id &&
              !isAuthIdInOrgGroups(row.last_auth_id, groups)
            }
          />
        ),
      },
      {
        field: 'firmware_revision',
        headerName: t(StringKeys.FIRMWARE_VERSION),
        minWidth: 140,
        flex: 0.5,
        display: 'flex',
        valueGetter: (_, row) => row.firmware_revision ?? '--',
        renderCell: ({ row }) => (
          <FirmwareRevisionDisplay
            firmwareRevision={row.firmware_revision}
            firmwareOutdated={isDeviceFirmwareOutdated(
              row.firmware_revision,
              firmwareLookup,
            )}
          />
        ),
      },
    ],
    [t, groups, firmwareLookup],
  );

  if (isMobile) {
    const q = searchQuery.toLowerCase();
    const filtered = devices.filter(
      (d) =>
        (d.serial_number ?? '').toLowerCase().includes(q) ||
        d.mac_address.toLowerCase().includes(q) ||
        (d.last_user_display_name ?? '').toLowerCase().includes(q),
    );
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <TextField
          label={t(StringKeys.SEARCH_DEVICES)}
          variant="outlined"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            },
          }}
          sx={{ mb: 2 }}
        />
        <Stack spacing={2} pb={5}>
          {filtered.map((device) => (
            <Card
              key={device.mac_address}
              role="button"
              tabIndex={0}
              aria-label={`${device.serial_number ?? device.mac_address}`}
              onClick={() => openDevice(device)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openDevice(device);
                }
              }}
              sx={{ cursor: 'pointer', boxShadow: 1, overflow: 'hidden' }}
            >
              <Box
                sx={{
                  backgroundColor: COLORS.SAFEGUARD_BLUE,
                  color: COLORS.WHITE,
                  p: 2,
                }}
              >
                <Typography variant="h6">
                  {formatDeviceTypeLabel(device.hardware_version)}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.5,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.SERIAL_NUMBER)}:</Typography>
                  <Typography>{device.serial_number ?? '--'}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.MAC_ADDRESS)}:</Typography>
                  <Typography>{device.mac_address}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.DEVICE_TYPE)}:</Typography>
                  <Typography>
                    {formatDeviceTypeLabel(device.hardware_version)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography>{t(StringKeys.BATTERY)}:</Typography>
                  <BatteryIndicator
                    percentage={device.battery_percentage ?? 0}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.DEVICE_LAST_CONNECTED)}:</Typography>
                  <Typography>{formatLastSeenAt(device.last_seen_at)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.LAST_USER)}:</Typography>
                  <LastUserCell
                    displayName={device.last_user_display_name}
                    showLeftOrgIcon={
                      !!device.last_auth_id &&
                      !isAuthIdInOrgGroups(device.last_auth_id, groups)
                    }
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Typography>{t(StringKeys.FIRMWARE_VERSION)}:</Typography>
                  <FirmwareRevisionDisplay
                    firmwareRevision={device.firmware_revision}
                    firmwareOutdated={isDeviceFirmwareOutdated(
                      device.firmware_revision,
                      firmwareLookup,
                    )}
                  />
                </Box>
              </Box>
            </Card>
          ))}
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 280px)',
        width: '100%',
        '@media (max-height: 620px)': { height: '80vh' },
      }}
    >
      <NavigatorDataGrid
        rows={devices}
        columns={columns}
        getRowId={(row) => row.mac_address}
        onRowClick={(params) => openDevice(params.row)}
        initialPageSize={DEFAULT_PAGE_SIZE}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        showToolbar={true}
        height="100%"
        slots={{ toolbar: GridToolbar }}
        columnVisibilityModel={{ mac_address: false }}
        slotProps={{
          toolbar: {
            printOptions: { disableToolbarButton: true },
            csvOptions: { disableToolbarButton: true },
            showQuickFilter: true,
            quickFilterProps: { debounceMs: 500 },
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: COLORS.SAFEGUARD_BLUE,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: COLORS.WHITE,
            fontWeight: 'bold',
            whiteSpace: 'normal',
            lineHeight: 'normal',
          },
        }}
      />
    </Box>
  );
};

export default DeviceTable;
